import { useState, type JSX, type ReactElement } from "react";

import type { ClipIcon, ClipTextSpan, ClipTipCard } from "@shared/clipSidecar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLeagueTip } from "@/clip/leagueTipFetch";

/**
 * Hover card for an item, spell, or rune: icon top-left, name, description in
 * Riot's accent colours, and the gold cost.
 *
 * Tip bodies come from Data Dragon on first hover (tipId + match.ddragonVersion),
 * not from clip.json — same layout as the app's LeagueAssetTip.
 */

const TipIcon = ({ src, alt }: { src: string; alt: string }): JSX.Element => (
  <span className="block size-10 shrink-0 overflow-hidden rounded-md bg-muted/70 ring-1 ring-border/80">
    <img
      src={src}
      alt={alt}
      decoding="async"
      draggable={false}
      referrerPolicy="no-referrer"
      className="size-full object-cover"
    />
  </span>
);

const ColouredDescription = ({ spans }: { spans: ClipTextSpan[] }): JSX.Element | null => {
  // Trim the leading and trailing blank runs Riot's markup leaves behind.
  const trimmed = spans
    .map((span, index) => {
      if (index === 0) return { ...span, text: span.text.replace(/^\n+/, "") };
      if (index === spans.length - 1) return { ...span, text: span.text.replace(/\n+$/, "") };
      return span;
    })
    .filter((span) => span.text.length > 0);
  if (trimmed.length === 0) return null;

  return (
    <p className="mt-1.5 text-[11px] leading-snug whitespace-pre-line text-popover-foreground/90">
      {trimmed.map((span, index) => (
        <span
          key={index}
          // Inline colour rather than a class: the palette comes from Data Dragon,
          // and Tailwind cannot generate a class it never sees.
          style={{
            ...(span.color ? { color: span.color } : {}),
            ...(span.bold ? { fontWeight: 600 } : {})
          }}
        >
          {span.text}
        </span>
      ))}
    </p>
  );
};

const TipBody = ({
  icon,
  tip,
  loading
}: {
  icon: ClipIcon;
  tip: ClipTipCard | null;
  loading: boolean;
}): JSX.Element => {
  const title = tip?.title ?? icon.alt ?? "…";
  return (
    <div className="flex gap-2.5">
      <TipIcon src={icon.url} alt={title} />
      <div className="min-w-0 flex-1">
        {tip?.eyebrow ? (
          <div className="mb-0.5 text-[10px] font-semibold tracking-wide text-sky-400">
            {tip.eyebrow}
          </div>
        ) : null}
        <div
          className={
            tip?.eyebrow ? "text-sm font-bold text-sky-400" : "text-sm font-semibold text-sky-300"
          }
        >
          {title}
        </div>
        {loading && !tip ? (
          <p className="mt-1.5 text-[11px] text-muted-foreground">Loading…</p>
        ) : (
          <ColouredDescription spans={tip?.body ?? []} />
        )}
        {tip?.cost ? (
          <div className="mt-2 text-[12px] leading-none font-semibold text-[#c8aa6e] tabular-nums">
            Cost: {tip.cost.total}
            <span className="font-medium text-[#c8aa6e]/85"> ({tip.cost.base})</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Wraps a trigger in the asset hover card. Fetches the tip on first open when
 * the icon carries a tipId and the match has a Data Dragon version.
 */
export const ClipAssetTip = ({
  icon,
  ddragonVersion,
  children,
  side = "top"
}: {
  icon: ClipIcon;
  ddragonVersion?: string | null;
  children: ReactElement;
  side?: "top" | "right" | "bottom" | "left";
}): JSX.Element => {
  const tipId = icon.tipId ?? null;
  const [open, setOpen] = useState(false);
  const { tip, loading } = useLeagueTip(tipId, ddragonVersion, open);
  const contentKey = `${tipId ?? ""}:${tip?.title ?? icon.alt ?? ""}:${icon.url}`;

  return (
    // Nested provider: zero open delay + no skip window so adjacent icons
    // swap tips immediately instead of leaving the previous one on screen.
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip disableHoverableContent onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 cursor-help">{children}</span>
        </TooltipTrigger>
        <TooltipContent key={contentKey} side={side} className="max-w-72 p-1.5" showCaret>
          <TipBody icon={icon} tip={tip} loading={loading} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
